import { useMemo, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { toast } from 'sonner';
import {
  analyzeDeal,
  DEAL_MARGIN_TARGET,
  DEAL_REGISTRATION_RATES,
  DEAL_SCENARIOS,
  ENGINE_DEAL_2026,
  type DealComparison,
  type DealResult,
  type DealScenarioName,
} from '@app/finance';
import type { DealSimulationInput } from '@app/shared';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { formatEuros, formatRatio } from '@/lib/format';
import { apiErrorMessage } from '@/lib/api';
import { percentToRatio } from '@/lib/rate';
import { useCreateSimulation } from './queries';
import { Field } from './Field';

export const DEAL_SCENARIO_LABELS: Record<DealScenarioName, string> = {
  pessimistic: 'Pessimiste',
  realistic: 'Réaliste',
  optimistic: 'Optimiste',
};

const formSchema = z.object({
  purchasePrice: z.number().positive('Requis'),
  surfaceM2: z.number().positive('Requis'),
  works: z.number().min(0),
  contingencyRate: z.number().min(0).max(100),
  commissionRate: z.number().min(0).max(100),
  financingRate: z.number().min(0).max(25),
  equityShare: z.number().min(0).max(100),
  carryMonths: z.number().int().min(1).max(60),
  monthlyCarryingCharges: z.number().min(0),
  pessimistic: z.number().min(0),
  realistic: z.number().min(0),
  optimistic: z.number().min(0),
  vatRegime: z.enum(['none', 'margin']),
  vatRate: z.number().min(0).max(100),
  corporateTaxRate: z.number().min(0).max(100),
  reducedCorporateTaxRate: z.boolean(),
});

type FormValues = z.infer<typeof formSchema>;

const DEFAULT_VALUES: FormValues = {
  purchasePrice: 100_000,
  surfaceM2: 50,
  works: 17_000,
  contingencyRate: 4,
  commissionRate: 5,
  financingRate: 3.8,
  equityShare: 10,
  carryMonths: 8,
  monthlyCarryingCharges: 150,
  pessimistic: 135_000,
  realistic: 151_000,
  optimistic: 160_000,
  vatRegime: 'none',
  vatRate: 20,
  corporateTaxRate: 25,
  reducedCorporateTaxRate: true,
};

export function toDealInput(values: FormValues): DealSimulationInput {
  return {
    operator: 'professional',
    purchasePrice: values.purchasePrice,
    surfaceM2: values.surfaceM2,
    works: values.works,
    contingencyRate: percentToRatio(values.contingencyRate),
    commissionRate: percentToRatio(values.commissionRate),
    financingRate: percentToRatio(values.financingRate),
    equityShare: percentToRatio(values.equityShare),
    carryMonths: values.carryMonths,
    monthlyCarryingCharges: values.monthlyCarryingCharges,
    resalePrices: { pessimistic: values.pessimistic, realistic: values.realistic, optimistic: values.optimistic },
    vatRegime: values.vatRegime,
    vatRate: percentToRatio(values.vatRate),
    corporateTaxRate: percentToRatio(values.corporateTaxRate),
    reducedCorporateTaxRate: values.reducedCorporateTaxRate,
  };
}

const RESULT_ROWS: { label: string; kind: 'euros' | 'percent'; get: (r: DealResult) => number; strong?: boolean }[] = [
  { label: 'Prix de revente (FAI)', kind: 'euros', get: (r) => r.revenue },
  { label: 'Prix de revente au m²', kind: 'euros', get: (r) => r.pricePerM2 },
  { label: 'Commission de revente', kind: 'euros', get: (r) => -r.resaleCommission },
  { label: 'Prix d’acquisition + droits', kind: 'euros', get: (r) => -r.acquisitionCost },
  { label: 'Travaux (aléas inclus)', kind: 'euros', get: (r) => -r.worksCost },
  { label: 'Frais financiers', kind: 'euros', get: (r) => -r.financingCost },
  { label: 'Charges de portage', kind: 'euros', get: (r) => -r.carryingCharges },
  { label: 'Coût de revient total', kind: 'euros', get: (r) => r.totalCost },
  { label: 'Marge avant impôt', kind: 'euros', get: (r) => r.grossMargin, strong: true },
  { label: 'Marge sur coût de revient', kind: 'percent', get: (r) => r.grossMarginRate, strong: true },
  { label: 'TVA sur marge', kind: 'euros', get: (r) => -r.vat },
  { label: 'Résultat imposable', kind: 'euros', get: (r) => r.taxableResult },
  { label: 'Impôt sur les sociétés', kind: 'euros', get: (r) => -r.corporateTax },
  { label: 'Marge nette après impôt', kind: 'euros', get: (r) => r.netMargin, strong: true },
  { label: 'Marge nette sur coût de revient', kind: 'percent', get: (r) => r.netMarginRate, strong: true },
];

export function DealResultsTable({ comparison }: { comparison: DealComparison }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[520px] border-collapse text-sm">
        <thead>
          <tr className="border-b border-border">
            <th className="p-2 text-left font-medium text-muted-foreground">Ligne</th>
            {DEAL_SCENARIOS.map((scenario) => (
              <th key={scenario} className="p-2 text-right font-medium">
                {DEAL_SCENARIO_LABELS[scenario]}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {RESULT_ROWS.map((row) => (
            <tr key={row.label} className={`border-b border-border/50 ${row.strong ? 'font-semibold' : ''}`}>
              <td className={`p-2 ${row.strong ? '' : 'text-muted-foreground'}`}>{row.label}</td>
              {DEAL_SCENARIOS.map((scenario) => {
                const value = row.get(comparison.scenarios[scenario]);
                return (
                  <td key={scenario} className="p-2 text-right tabular-nums">
                    {row.kind === 'percent' ? formatRatio(value) : formatEuros(value)}
                  </td>
                );
              })}
            </tr>
          ))}
          <tr className="border-t-2 border-border">
            <td className="p-2 text-muted-foreground">Seuil de {formatRatio(DEAL_MARGIN_TARGET)} (avant impôt)</td>
            {DEAL_SCENARIOS.map((scenario) => {
              const ok = comparison.scenarios[scenario].meetsTarget;
              return (
                <td key={scenario} className={`p-2 text-right ${ok ? 'text-foreground' : 'text-destructive'}`}>
                  {ok ? 'Atteint' : 'Non atteint'}
                </td>
              );
            })}
          </tr>
        </tbody>
      </table>
    </div>
  );
}

export function DealForm() {
  const [label, setLabel] = useState('Achat-revente — ');
  const { register, control } = useForm<FormValues>({ defaultValues: DEFAULT_VALUES });
  const values = useWatch({ control });
  const createSimulation = useCreateSimulation();

  const comparison = useMemo(() => {
    const parsed = formSchema.safeParse(values);
    if (!parsed.success) return null;
    return analyzeDeal(toDealInput(parsed.data));
  }, [values]);

  const handleSave = () => {
    const parsed = formSchema.safeParse(values);
    if (!parsed.success) {
      toast.error('Corrigez les champs invalides avant d’enregistrer.');
      return;
    }
    createSimulation.mutate(
      { kind: 'deal', label: label.trim() || 'Achat-revente', engineVersion: ENGINE_DEAL_2026, input: toDealInput(parsed.data) },
      {
        onSuccess: () => toast.success('Simulation enregistrée'),
        onError: (error) => toast.error(apiErrorMessage(error, 'Échec de l’enregistrement')),
      },
    );
  };

  return (
    <div className="flex flex-col gap-6">
      <form className="flex flex-col gap-4">
        <p className="text-sm text-muted-foreground">
          Statut : professionnel (marchand de biens) — droits d’enregistrement de{' '}
          {formatRatio(DEAL_REGISTRATION_RATES.professional)} du prix d’acquisition, régime de faveur de l’art. 1115 CGI.
        </p>

        <fieldset className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <legend className="col-span-full text-sm font-medium text-muted-foreground">Acquisition et travaux</legend>
          <Field label="Prix d'acquisition (€)" htmlFor="purchasePrice">
            <Input id="purchasePrice" type="number" step="1" min="0" {...register('purchasePrice', { valueAsNumber: true })} />
          </Field>
          <Field label="Surface (m²)" htmlFor="surfaceM2">
            <Input id="surfaceM2" type="number" step="0.1" min="0" {...register('surfaceM2', { valueAsNumber: true })} />
          </Field>
          <Field label="Travaux hors aléas (€)" htmlFor="works">
            <Input id="works" type="number" step="1" min="0" {...register('works', { valueAsNumber: true })} />
          </Field>
          <Field label="Aléas sur travaux (%)" htmlFor="contingencyRate">
            <Input id="contingencyRate" type="number" step="0.1" min="0" {...register('contingencyRate', { valueAsNumber: true })} />
          </Field>
        </fieldset>

        <fieldset className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <legend className="col-span-full text-sm font-medium text-muted-foreground">Portage et financement</legend>
          <Field label="Durée de portage (mois)" htmlFor="carryMonths">
            <Input id="carryMonths" type="number" step="1" min="1" {...register('carryMonths', { valueAsNumber: true })} />
          </Field>
          <Field label="Taux du crédit (%)" htmlFor="financingRate">
            <Input id="financingRate" type="number" step="0.01" min="0" {...register('financingRate', { valueAsNumber: true })} />
          </Field>
          <Field label="Apport en fonds propres (%)" htmlFor="equityShare">
            <Input id="equityShare" type="number" step="1" min="0" {...register('equityShare', { valueAsNumber: true })} />
          </Field>
          <Field label="Charges non récupérables (€/mois)" htmlFor="monthlyCarryingCharges">
            <Input id="monthlyCarryingCharges" type="number" step="1" min="0" {...register('monthlyCarryingCharges', { valueAsNumber: true })} />
          </Field>
        </fieldset>

        <fieldset className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <legend className="col-span-full text-sm font-medium text-muted-foreground">Revente (FAI, trois scénarios)</legend>
          <Field label="Prix pessimiste (€)" htmlFor="pessimistic">
            <Input id="pessimistic" type="number" step="1" min="0" {...register('pessimistic', { valueAsNumber: true })} />
          </Field>
          <Field label="Prix réaliste (€)" htmlFor="realistic">
            <Input id="realistic" type="number" step="1" min="0" {...register('realistic', { valueAsNumber: true })} />
          </Field>
          <Field label="Prix optimiste (€)" htmlFor="optimistic">
            <Input id="optimistic" type="number" step="1" min="0" {...register('optimistic', { valueAsNumber: true })} />
          </Field>
          <Field label="Commission de revente (% du prix)" htmlFor="commissionRate">
            <Input id="commissionRate" type="number" step="0.1" min="0" {...register('commissionRate', { valueAsNumber: true })} />
          </Field>
        </fieldset>

        <fieldset className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <legend className="col-span-full text-sm font-medium text-muted-foreground">Fiscalité de sortie</legend>
          <Field label="TVA sur la revente" htmlFor="vatRegime">
            <Controller
              control={control}
              name="vatRegime"
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger className="w-full" id="vatRegime">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Hors champ (ancien, droits payés par l’acquéreur)</SelectItem>
                    <SelectItem value="margin">TVA sur marge</SelectItem>
                  </SelectContent>
                </Select>
              )}
            />
          </Field>
          <Field label="Taux de TVA (%)" htmlFor="vatRate">
            <Input id="vatRate" type="number" step="0.1" min="0" {...register('vatRate', { valueAsNumber: true })} />
          </Field>
          <Field label="Impôt sur les sociétés — taux normal (%)" htmlFor="corporateTaxRate">
            <Input id="corporateTaxRate" type="number" step="0.1" min="0" {...register('corporateTaxRate', { valueAsNumber: true })} />
          </Field>
          <div className="flex items-center gap-2 sm:col-span-3">
            <input id="reducedCorporateTaxRate" type="checkbox" {...register('reducedCorporateTaxRate')} />
            <label htmlFor="reducedCorporateTaxRate" className="text-sm">
              Taux réduit de 15 % jusqu’à 42 500 € de résultat (conditions de capital à vérifier)
            </label>
          </div>
        </fieldset>
      </form>

      {comparison ? (
        <DealResultsTable comparison={comparison} />
      ) : (
        <p className="text-sm text-muted-foreground">Corrigez les champs invalides pour voir le résultat.</p>
      )}

      <div className="flex flex-wrap items-end gap-3">
        <Field label="Nom de la simulation" htmlFor="label">
          <Input id="label" value={label} onChange={(event) => setLabel(event.target.value)} className="w-64" />
        </Field>
        <Button onClick={handleSave} disabled={createSimulation.isPending || !comparison}>
          {createSimulation.isPending ? 'Enregistrement…' : 'Enregistrer cette simulation'}
        </Button>
      </div>
    </div>
  );
}
